/**
 * useStoreDispatch Hook
 * 
 * Library-agnostic dispatch hook that replaces useAppDispatch.
 * Uses the store from context to dispatch actions and thunks.
 */

import { useMemo } from 'react';
import { useStoreContext } from '../context/StoreContext';
import type { IAction, Thunk } from '../types';

/**
 * Hook to dispatch actions or thunks to the store
 * 
 * @example
 * ```typescript
 * const dispatch = useStoreDispatch();
 * dispatch({ type: 'counter/increment' });
 * dispatch((dispatch, getState) => { ... }); // thunk
 * ```
 */
export function useStoreDispatch<State = unknown>(): <T = unknown>(
  action: IAction<T> | Thunk<State, T>,
) => IAction<T> | T | unknown {
  const { store } = useStoreContext<State>();

  return useMemo(() => {
    return <T = unknown>(action: IAction<T> | Thunk<State, T>) => store.dispatch(action);
  }, [store]);
}
